import React, { useEffect, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import CountryCityTab from "./CountryCityTab";
import "./index.css";
import { styled } from "@mui/system";
import { ReactComponent as GlobeIcon } from "../../assets/world-globe-language.svg";
import { ReactComponent as Union } from "../../assets/Union.svg";
import { ReactComponent as GeneralIcon } from "../../assets/GeneralIcon.svg";
import { ReactComponent as BookOpen } from "../../assets/BookOpen.svg";
import { ReactComponent as Vector } from "../../assets/Vector.svg";
import { ReactComponent as TruckDelivery } from "../../assets/truck-delivery.svg";
import General from "./General";
import CleanUp from "./CleanUp";
import DriversTab from "./DriversTab";
import Archive from "./Archive";
import PriceConfigurations from "./PriceConfigurations";
import DriverPayments from "./DriverPayments";
import UserRoleAssigments from "./UserRoleAssigments";
import { UserAccess } from "../../types/UserAccess";
import api from "../../utils/axios";
import { getUserRoleFromToken } from "../../utils/Auth";
import UserRoleManagement from "./UserRoleManagement";
import CurrencyManagement from "./CurrencyManagement";
import useLang from "../../hooks/useLang";
import InternationalOrders from "./InternationalOrders";
import DynamicReport from "./DynamicReport";
import ChangeGoogleMapsKey from "./ChangeGoogleMapsKey";
import IncorrectInsertedOrders from "../Order/IncorrectInsertedOrders/IncorrectInsertedOrders";
import SortingMachineTab from "./SortingMachineTab/SortingMachineTab";

const StyledTab = styled(Tab)({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",
	gap: "0.25rem",
});

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

interface TabMenuProps {
	id: number;
	label: string;
	icon?: React.ReactElement;
	index: number;
	panel: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
	return (
		<div role="tabpanel" hidden={value !== index}>
			{value === index && <Box p={3}>{children}</Box>}
		</div>
	);
};

const MyTabs: React.FC = () => {
	const { strings } = useLang("arrangementIndexTabs");

	const isMobile = useMediaQuery("(max-width:600px), (min-width:600px)");

	const [value, setValue] = useState(0);
	const [tabList, setTabList] = useState<TabMenuProps[]>([]);

	const getAllowedUserTabList = async (
		initialTabList: TabMenuProps[]
	): Promise<TabMenuProps[]> => {
		const roleId = getUserRoleFromToken();
		const response = await api.get("Menu/select-menus", {
			params: { roleId },
		});
		let allowedTabList: UserAccess[] = [];
		if (response.status === 200 && response.data) {
			allowedTabList = response?.data.filter(
				(element: UserAccess) => element.access === true
			);
		}
		const filteredTabList: TabMenuProps[] = initialTabList.filter((tab) =>
			allowedTabList
				.map((element: UserAccess) => element.id)
				.includes(tab.id)
		);
		return filteredTabList;
	};

	useEffect(() => {
		if (!strings) {
			return;
		}
		(async () => {
			const initialTabList: TabMenuProps[] = [
				{
					id: 27,
					label: strings?.shtetetQytetet,
					icon: <GlobeIcon />,
					index: 0,
					panel: <CountryCityTab />,
				},
				{
					id: 103,
					label: strings?.activeSortingSession,
					icon: <GeneralIcon />,
					index: 11,
					panel: <SortingMachineTab />,
				},
				{
					id: 28,
					label: strings?.konfigurimetCmimet,
					icon: <Union />,
					index: 1,
					panel: <PriceConfigurations />,
				},
				{
					id: 29,
					label: strings?.tePergjithshme,
					icon: <GeneralIcon />,
					index: 2,
					panel: <General />,
				},
				{
					id: 30,
					label: strings?.arkivimi,
					icon: <BookOpen />,
					index: 3,
					panel: <Archive />,
				},
				{
					id: 31,
					label: strings?.pastrimi,
					icon: <Vector />,
					index: 4,
					panel: <CleanUp />,
				},

				{
					id: 33,
					label: strings?.pagesatShofereve,
					icon: <TruckDelivery />,
					index: 6,
					panel: <DriverPayments />,
				},
				{
					id: 34,
					label: strings?.caktimiMeneve,
					icon: <GeneralIcon />,
					index: 7,
					panel: <UserRoleAssigments />,
				},
				{
					id: 35,
					label: strings?.menaxhimiRoleve,
					icon: <GeneralIcon />,
					index: 8,
					panel: <UserRoleManagement />,
				},
				{
					id: 36,
					label: "Currency",
					icon: <GeneralIcon />,
					index: 9,
					panel: <CurrencyManagement />,
				},
				{
					id: 54,
					label: "Porosite internacionale",
					icon: <GeneralIcon />,
					index: 10,
					panel: <InternationalOrders />,
				},
				{
					id: 59,
					label: "Raportet Dinamike",
					icon: <GeneralIcon />,
					index: 10,
					panel: <DynamicReport />,
				},
				{
					id: 60,
					label: "Google Maps Key",
					icon: <GeneralIcon />,
					index: 10,
					panel: <ChangeGoogleMapsKey />,
				},

				{
					id: 87,
					label: "Insert int. Incorrect Orders",
					icon: <GeneralIcon />,
					index: 13,
					panel: <IncorrectInsertedOrders />,
				},
			];
			const filteredTabList: TabMenuProps[] =
				await getAllowedUserTabList(initialTabList);
			const filteredTabListFixedIndexes: TabMenuProps[] =
				filteredTabList.map((element: TabMenuProps, index) => ({
					id: element.id,
					label: element.label,
					icon: element.icon,
					index,
					panel: element.panel,
				}));
			setTabList(filteredTabListFixedIndexes);
		})();
	}, [strings]);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	return (
		<div className="mainTabsContainer">
			<div className="tabsContainer">
				<Tabs
					value={value}
					onChange={handleChange}
					variant={isMobile ? "scrollable" : "standard"}
					scrollButtons="auto"
					centered={!isMobile}
					allowScrollButtonsMobile
				>
					{tabList.map((element: TabMenuProps) => (
						<StyledTab
							key={element.id}
							label={element.label}
							icon={element.icon}
						/>
					))}
				</Tabs>
				{tabList.map((element: TabMenuProps) => (
					<TabPanel
						key={element.id}
						value={value}
						index={element.index}
					>
						{element.panel}
					</TabPanel>
				))}
			</div>
		</div>
	);
};

export default MyTabs;
